/**
 * Which local project does an `Open locally` link mean?
 *
 * `parseOpenLocalLink` proves a link is well-formed. It cannot prove the sender
 * had any business naming that repo, and it says nothing about where that repo
 * lives on this machine. This module answers the second question, and it is
 * kept pure: the caller supplies the candidate projects and the remote URL it
 * already resolved for each one (server-side via `RepositoryIdentityResolver`),
 * so nothing here shells out to git.
 *
 * ## Why ambiguity is a refusal, not a tiebreak
 *
 * Forge creates git worktrees routinely, and every worktree of a repo reports
 * the same remote. So "which project is `owner/name`?" genuinely can have more
 * than one right answer, and the candidates are not ranked by anything the link
 * knows about — a worktree is not more or less correct than the main checkout.
 *
 * Picking the first match would open an arbitrary worktree and check a branch
 * out in it. That is the same first-one-wins failure already recorded against
 * the sidebar catalog, and it is worse here because it mutates a checkout the
 * user was not looking at. So an ambiguous link returns every candidate and
 * lets the caller ask. Refusing to guess is the same stance the link validator
 * takes.
 */

import { parseGitHubRepositoryNameWithOwnerFromRemoteUrl } from "./git";

export interface OpenLocalCandidate {
  readonly projectId: string;
  readonly workspaceRoot: string;
  /** The project's primary remote, or `null` when it has none (or is not a repo). */
  readonly remoteUrl: string | null;
}

export interface OpenLocalMatch {
  readonly projectId: string;
  readonly workspaceRoot: string;
}

export type OpenLocalTarget =
  | { readonly ok: true; readonly match: OpenLocalMatch }
  | { readonly ok: false; readonly reason: "no-matching-project" }
  | {
      readonly ok: false;
      readonly reason: "ambiguous-match";
      readonly candidates: readonly OpenLocalMatch[];
    };

/**
 * GitHub treats `owner/name` case-insensitively, and remote URLs are written
 * both ways in practice, so comparison is case-folded. This is the one place
 * the module normalizes anything — and it normalizes only for the comparison,
 * never for what gets returned.
 */
function sameRepo(left: string, right: string): boolean {
  return left.toLowerCase() === right.toLowerCase();
}

export function findOpenLocalCandidates(
  repoFullName: string,
  candidates: readonly OpenLocalCandidate[],
): readonly OpenLocalMatch[] {
  const matches: OpenLocalMatch[] = [];
  const seenWorkspaceRoots = new Set<string>();

  for (const candidate of candidates) {
    const candidateRepo = parseGitHubRepositoryNameWithOwnerFromRemoteUrl(candidate.remoteUrl);
    if (!candidateRepo || !sameRepo(candidateRepo, repoFullName)) continue;
    // The same directory registered twice is one place to open, not an
    // ambiguity worth interrupting the user over.
    if (seenWorkspaceRoots.has(candidate.workspaceRoot)) continue;
    seenWorkspaceRoots.add(candidate.workspaceRoot);
    matches.push({ projectId: candidate.projectId, workspaceRoot: candidate.workspaceRoot });
  }

  return matches;
}

export function resolveOpenLocalTarget(
  repoFullName: string,
  candidates: readonly OpenLocalCandidate[],
): OpenLocalTarget {
  const matches = findOpenLocalCandidates(repoFullName, candidates);
  const [only] = matches;
  if (!only) return { ok: false, reason: "no-matching-project" };
  if (matches.length > 1) return { ok: false, reason: "ambiguous-match", candidates: matches };
  return { ok: true, match: only };
}
